import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, AlertCircle, History, FilePlus, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import IndexWorkspaceShell from '@/components/index/IndexWorkspaceShell';
import { formatMandateId, formatScriptId } from '@/utils/formatIndexId';
import WorkflowStepper from '@/components/workflow/WorkflowStepper';
import WorkflowChoiceDialog from '@/components/workflow/WorkflowChoiceDialog';
import ScriptUploadDialog, { type ScriptUploadResult } from '@/components/workflow/ScriptUploadDialog';
import MethodologyPendingBanner from '@/components/MethodologyPendingBanner';
import ScorecardSelectionModal from '@/components/ScorecardSelectionModal';
import MandateConfirmDialog from '@/components/MandateConfirmDialog';
import { useWorkflowSession } from '@/hooks/useWorkflowSession';
import { useAnalysisProgress } from '@/hooks/useAnalysisProgress';
import { getWorkflow, markGenerateScorecardComplete } from '@/services/workflowService';
import { getScorecardById } from '@/services/scorecardService';
import { getEffectiveMandate, getProject } from '@/services/projectService';
import { fetchMethodologyGateStatus } from '@/services/methodologyStatus';
import { methodologyPhaseLabel } from '@/config/methodologyAnalysisPipeline';
import type { WorkflowScriptListItem, WorkflowStepKey, ScriptWorkflowState } from '@/types/workflow';
import { getInflightAnalysis } from '@/utils/inflightAnalysisStorage';
import {
  getActiveProjectId,
  setActiveProjectId,
} from '@/utils/projectWorkflowStorage';
import { onIndexShortcut } from '@/hooks/useIndexShortcuts';
import {
  EMPTY_DEVELOPMENT_MANDATE,
  normalizeMandate,
  type DevelopmentMandateSelections,
} from '@/config/developmentMandate';

const WORKFLOW_SESSION_KEY = 'workflow_active_session_id';

function formatAggregateScore(overall: number | undefined | null): string | null {
  if (overall == null || Number.isNaN(overall)) return null;
  return (overall / 10).toFixed(1);
}

const Workflow: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [projectId, setProjectId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string>('');
  const [mandateConfirmOpen, setMandateConfirmOpen] = useState(false);
  const [effectiveMandate, setEffectiveMandate] = useState<DevelopmentMandateSelections>({
    ...EMPTY_DEVELOPMENT_MANDATE,
  });
  const [mandateLoading, setMandateLoading] = useState(false);
  const {
    workflow,
    loading: workflowLoading,
    error: workflowError,
    applyWorkflow,
    resetToNew,
    setStep,
    refresh,
    setWorkflow,
  } = useWorkflowSession();

  const [showChoice, setShowChoice] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [showScorecardModal, setShowScorecardModal] = useState(false);
  const [scriptTotalScore, setScriptTotalScore] = useState<string | null>(null);
  const [methodologyEnabled, setMethodologyEnabled] = useState<boolean | null>(null);
  const [methodologyMessage, setMethodologyMessage] = useState<string | null>(null);

  const uploadMetaRef = useRef<{
    gcs_path?: string;
    script_name?: string;
  }>({});

  const handleAnalysisComplete = useCallback(
    async (analysisSessionId: string) => {
      setStep('analyse', 'complete');
      setWorkflow((prev) => ({
        ...prev,
        analysis_session_id: analysisSessionId,
        steps: { ...prev.steps, analyse: 'complete' },
      }));
      try {
        const updated = await refresh(analysisSessionId);
        sessionStorage.setItem(WORKFLOW_SESSION_KEY, analysisSessionId);
        applyWorkflow(updated);
      } catch {
        sessionStorage.setItem(WORKFLOW_SESSION_KEY, analysisSessionId);
      }
      // Gate scorecard behind step-output review.
      navigate(`/workflow/steps/${analysisSessionId}`);
    },
    [applyWorkflow, navigate, refresh, setStep, setWorkflow],
  );

  const handleAnalysisCancelled = useCallback(() => {
    setStep('analyse', 'not_started');
  }, [setStep]);

  const {
    analyzing,
    analyseSubtext,
    analysisSessionId,
    error: analysisError,
    warning: analysisWarning,
    failedStepIds,
    scorecardPending,
    startAnalysis,
    cancel,
    reset,
    resumeIfInProgress,
    setAnalysisSessionId,
  } = useAnalysisProgress({
    onComplete: handleAnalysisComplete,
    onCancelled: handleAnalysisCancelled,
    onError: () => setStep('analyse', 'not_started'),
  });

  useEffect(() => {
    let cancelled = false;
    void fetchMethodologyGateStatus()
      .then((gate) => {
        if (cancelled) return;
        setMethodologyEnabled(gate.methodology_enabled);
        setMethodologyMessage(gate.message ?? null);
      })
      .catch(() => {
        if (!cancelled) setMethodologyEnabled(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return onIndexShortcut('audit-run', () => {
      if (analyzing) return;
      if (workflow.steps.upload !== 'complete') {
        setShowChoice(false);
        setShowUpload(true);
      } else if (workflow.steps.analyse !== 'complete') {
        setMandateConfirmOpen(true);
      }
    });
  }, [analyzing, workflow.steps]);

  const resumeWorkflowAnalysis = useCallback(
    (data: ScriptWorkflowState, savedId: string) => {
      if (data.steps.analyse === 'complete') return;

      const inflight = getInflightAnalysis();
      const workflowId = data.upload_session_id ?? savedId;
      const analysisId =
        data.analysis_session_id ||
        inflight?.analysisSessionId ||
        workflowId;
      const inflightMatches =
        !!inflight?.analysisSessionId &&
        (inflight.workflowSessionId === workflowId ||
          inflight.workflowSessionId === savedId ||
          inflight.analysisSessionId === analysisId ||
          inflight.analysisSessionId === data.analysis_session_id);

      const shouldResume =
        !!analysisId &&
        (data.steps.analyse === 'in_progress' || inflightMatches);

      if (!shouldResume) {
        // Backend may still say not_started while analysis_progress exists under upload id.
        if (data.steps.upload !== 'complete' || !analysisId) return;
        void (async () => {
          try {
            const { getAnalysisStatus } = await import('@/services/scriptService');
            const status = await getAnalysisStatus(analysisId);
            if (status.status === 'processing') {
              setStep('analyse', 'in_progress');
              setAnalysisSessionId(analysisId);
              void resumeIfInProgress(analysisId, {
                scriptTitle: data.script_title || inflight?.scriptTitle,
                workflowSessionId: workflowId,
              });
            } else if (
              status.status === 'completed' ||
              status.status_detail === 'completed_with_errors'
            ) {
              void handleAnalysisComplete(analysisId);
            }
          } catch {
            /* no progress — leave not_started */
          }
        })();
        return;
      }

      // Stepper reads workflow.steps.analyse — must set in_progress, not only analyzing.
      setStep('analyse', 'in_progress');
      setAnalysisSessionId(analysisId);
      void resumeIfInProgress(analysisId, {
        scriptTitle: data.script_title || inflight?.scriptTitle,
        workflowSessionId: workflowId,
      });
    },
    [handleAnalysisComplete, resumeIfInProgress, setAnalysisSessionId, setStep],
  );

  useEffect(() => {
    const fromQuery = searchParams.get('projectId');
    const fromStorage = getActiveProjectId();
    const resolved = fromQuery || fromStorage;
    if (!resolved) {
      navigate('/home', { replace: true });
      return;
    }
    setProjectId(resolved);
    setActiveProjectId(resolved);
    void getProject(resolved)
      .then((p) => setProjectName(p.name))
      .catch(() => setProjectName(''));
  }, [searchParams, navigate]);

  useEffect(() => {
    if (searchParams.get('newScript') !== '1' || !projectId) return;

    reset();
    resetToNew();
    sessionStorage.removeItem(WORKFLOW_SESSION_KEY);
    uploadMetaRef.current = {};
    setShowChoice(false);
    setShowUpload(true);

    const params = new URLSearchParams(searchParams);
    params.delete('newScript');
    navigate({ pathname: '/workflow', search: params.toString() }, { replace: true });
  }, [searchParams, projectId, navigate, reset, resetToNew]);

  useEffect(() => {
    const savedId = sessionStorage.getItem(WORKFLOW_SESSION_KEY);
    if (!savedId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getWorkflow(savedId);
        if (cancelled) return;
        applyWorkflow(data);
        uploadMetaRef.current = {
          gcs_path: data.gcs_path,
          script_name: data.script_title,
        };
        setShowChoice(false);
        resumeWorkflowAnalysis(data, savedId);
      } catch {
        sessionStorage.removeItem(WORKFLOW_SESSION_KEY);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyWorkflow, resumeWorkflowAnalysis]);

  const persistSessionId = (id: string) => {
    sessionStorage.setItem(WORKFLOW_SESSION_KEY, id);
  };

  const handleNewScript = () => {
    resetToNew();
    sessionStorage.removeItem(WORKFLOW_SESSION_KEY);
    uploadMetaRef.current = {};
    setShowChoice(false);
  };

  const handleStartNewAnalysis = () => {
    reset();
    resetToNew();
    sessionStorage.removeItem(WORKFLOW_SESSION_KEY);
    uploadMetaRef.current = {};
    setShowChoice(true);
  };

  const handleExistingSelect = async (item: WorkflowScriptListItem) => {
    applyWorkflow(item);
    uploadMetaRef.current = {
      gcs_path: item.gcs_path,
      script_name: item.script_title,
    };
    persistSessionId(item.session_id);
    setShowChoice(false);
    resumeWorkflowAnalysis(item, item.session_id);
    try {
      const updated = await refresh(item.session_id);
      uploadMetaRef.current = {
        gcs_path: updated.gcs_path ?? item.gcs_path,
        script_name: updated.script_title ?? item.script_title,
      };
    } catch {
      /* keep local state */
    }
  };

  const handleExistingScorecardSelect = async (scorecardId: string) => {
    try {
      const workflow = await getWorkflow(scorecardId);
      await handleExistingSelect({
        ...workflow,
        session_id: workflow.session_id ?? scorecardId,
      } as WorkflowScriptListItem);
    } catch {
      await handleExistingSelect({
        session_id: scorecardId,
        analysis_session_id: scorecardId,
        steps: {
          upload: 'complete',
          analyse: 'complete',
          generate_scorecard: 'not_started',
          rebuild: 'not_started',
        },
      } as WorkflowScriptListItem);
    }
  };

  const handleUploadSuccess = async (result: ScriptUploadResult) => {
    uploadMetaRef.current = {
      gcs_path: result.canonical_gcs_path || result.gcs_path,
      script_name: result.script_name,
    };
    setStep('upload', 'complete');
    setWorkflow((prev) => ({
      ...prev,
      upload_session_id: result.session_id,
      script_title: result.script_name,
      steps: { ...prev.steps, upload: 'complete' },
    }));
    persistSessionId(result.session_id);
    try {
      const updated = await refresh(result.session_id);
      uploadMetaRef.current.gcs_path = updated.gcs_path ?? result.gcs_path;
      applyWorkflow(updated);
    } catch {
      /* local state is sufficient */
    }
  };

  const runAnalysis = async () => {
    const uploadId = workflow.upload_session_id;
    if (!uploadId || !projectId) return;

    setStep('analyse', 'in_progress');
    await startAnalysis({
      uploadSessionId: uploadId,
      scriptText: '',
      gcsUri: uploadMetaRef.current.gcs_path || workflow.gcs_path,
      scriptName: workflow.script_title || uploadMetaRef.current.script_name,
      projectId,
    });
  };

  const handleStartAnalyse = async () => {
    if (workflow.steps.upload !== 'complete' || analyzing || !projectId) return;

    const uploadId = workflow.upload_session_id;
    if (!uploadId) return;

    try {
      setMandateLoading(true);
      const effective = await getEffectiveMandate(projectId);
      setEffectiveMandate(normalizeMandate(effective.effective_mandate));
      setProjectName(effective.project_name || projectName);
      setMandateConfirmOpen(true);
    } catch {
      setEffectiveMandate({ ...EMPTY_DEVELOPMENT_MANDATE });
      setMandateConfirmOpen(true);
    } finally {
      setMandateLoading(false);
    }
  };

  const handleAcceptMandate = async () => {
    setMandateConfirmOpen(false);
    await runAnalysis();
  };

  const scorecardTargetId = workflow.analysis_session_id || analysisSessionId;

  useEffect(() => {
    if (workflow.steps.analyse !== 'complete' || !scorecardTargetId) {
      setScriptTotalScore(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const detail = await getScorecardById(scorecardTargetId);
        if (cancelled) return;
        const overall =
          detail.analysis_metadata?.overall_score ??
          detail.scorecard?.overall_engagement_score;
        setScriptTotalScore(formatAggregateScore(overall));
      } catch {
        if (!cancelled) setScriptTotalScore(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scorecardTargetId, workflow.steps.analyse]);

  const navigateToScorecard = async () => {
    const id = scorecardTargetId;
    if (!id) return;
    try {
      await getScorecardById(id);
    } catch {
      // Keep Generate Scorecard incomplete until a real scorecard exists.
      navigate(`/workflow/steps/${id}`);
      return;
    }
    try {
      await markGenerateScorecardComplete(id);
      setStep('generate_scorecard', 'complete');
    } catch {
      /* navigation still proceeds once the scorecard is readable */
    }
    navigate(`/analysis/${id}`);
  };

  const canClickStep = (key: WorkflowStepKey): boolean => {
    if (key === 'upload') return workflow.steps.upload !== 'complete' && !analyzing;
    if (key === 'analyse') {
      return (
        workflow.steps.upload === 'complete' &&
        !analyzing &&
        workflow.steps.analyse !== 'in_progress' &&
        methodologyEnabled !== false
      );
    }
    // Ratings / scorecard agent is Step D — keep node visible but not clickable for methodology-only runs.
    if (key === 'generate_scorecard') {
      // After analysis, route through step review; scorecard view once marked complete.
      return (
        workflow.steps.analyse === 'complete' &&
        !analyzing &&
        !!scorecardTargetId &&
        (workflow.steps.generate_scorecard === 'complete'
          ? !scorecardPending && !!scriptTotalScore
          : true)
      );
    }
    if (key === 'rebuild') {
      return (
        workflow.steps.generate_scorecard === 'complete' &&
        workflow.steps.analyse === 'complete' &&
        !analyzing &&
        !!scorecardTargetId
      );
    }
    return false;
  };

  const handleRetryAnalyse = () => {
    if (analyzing || workflow.steps.upload !== 'complete') return;
    setStep('analyse', 'not_started');
    void handleStartAnalyse();
  };

  const handleStepClick = (key: WorkflowStepKey) => {
    if (key === 'upload' && workflow.steps.upload !== 'complete') {
      setShowUpload(true);
    } else if (key === 'analyse' && canClickStep('analyse')) {
      void handleStartAnalyse();
    } else if (key === 'generate_scorecard' && canClickStep('generate_scorecard') && scorecardTargetId) {
      if (workflow.steps.generate_scorecard === 'complete') {
        void navigateToScorecard();
      } else {
        navigate(`/workflow/steps/${scorecardTargetId}`);
      }
    } else if (key === 'rebuild' && canClickStep('rebuild') && scorecardTargetId) {
      navigate(`/workflow/rebuild/${scorecardTargetId}`);
    }
  };

  const hasAnyStepComplete = Object.values(workflow.steps).some((s) => s === 'complete');
  const showStartNewAnalysis = hasAnyStepComplete;
  const showReviewSteps =
    workflow.steps.analyse === 'complete' &&
    !!scorecardTargetId &&
    (workflow.steps.generate_scorecard !== 'complete' || scorecardPending || !scriptTotalScore);
  const showContinueToScorecard =
    workflow.steps.generate_scorecard === 'complete' &&
    !!scorecardTargetId &&
    !scorecardPending &&
    !!scriptTotalScore;

  // Keep stepper in sync while analyzing even if stored workflow briefly lags.
  const displaySteps = {
    ...workflow.steps,
    analyse:
      workflow.steps.analyse === 'complete'
        ? ('complete' as const)
        : analyzing || workflow.steps.analyse === 'in_progress'
          ? ('in_progress' as const)
          : workflow.steps.analyse,
  };

  const activeStep: WorkflowStepKey | null = analyzing || displaySteps.analyse === 'in_progress'
    ? 'analyse'
    : displaySteps.upload !== 'complete'
      ? 'upload'
      : displaySteps.analyse !== 'complete'
        ? 'analyse'
        : displaySteps.generate_scorecard !== 'complete'
          ? 'generate_scorecard'
          : displaySteps.rebuild !== 'complete'
            ? 'rebuild'
            : null;

  const mandateId = projectId ? formatMandateId(projectId) : null;
  const scriptId =
    workflow.session_id || workflow.analysis_session_id
      ? formatScriptId(workflow.session_id ?? workflow.analysis_session_id ?? '')
      : null;

  return (
    <IndexWorkspaceShell activeNav="audit">
      <WorkflowChoiceDialog
        open={showChoice}
        onNewScript={handleNewScript}
        onExistingScript={() => {
          setShowPicker(true);
        }}
        onCancel={() => {
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            navigate('/home');
          }
        }}
      />
      <ScriptUploadDialog
        open={showUpload}
        onOpenChange={setShowUpload}
        onUploadSuccess={handleUploadSuccess}
        projectId={projectId ?? undefined}
      />
      <MandateConfirmDialog
        open={mandateConfirmOpen}
        onOpenChange={setMandateConfirmOpen}
        projectName={projectName}
        effectiveMandate={effectiveMandate}
        onAccept={() => void handleAcceptMandate()}
        loading={analyzing || mandateLoading}
      />
      <ScorecardSelectionModal
        open={showPicker}
        onOpenChange={setShowPicker}
        onSelectForWorkflow={handleExistingScorecardSelect}
        projectId={projectId ?? undefined}
        title="Select a Script"
        description="Choose a previous analysis to continue your workflow."
      />
      <ScorecardSelectionModal
        open={showScorecardModal}
        onOpenChange={setShowScorecardModal}
      />

      <div className="mx-auto max-w-6xl">
        <header className="mb-8 flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <p className="ni-label mb-2">Module</p>
            <h1 className="text-2xl md:text-3xl font-sans font-bold text-white tracking-tight">
              Nuclius Audit
            </h1>
            <p className="mt-2 text-white/45 text-sm font-sans max-w-2xl">
              Ingest screenplays as canonical Markdown, then run the Nuclius Index methodology
              pipeline (step-level progress).
            </p>
            {methodologyEnabled === false && (
              <div className="mt-4 max-w-2xl">
                <MethodologyPendingBanner message={methodologyMessage} />
              </div>
            )}
            {analysisWarning && (
              <p className="mt-3 text-sm text-amber-200/90 font-sans max-w-2xl">{analysisWarning}</p>
            )}
            {(mandateId || scriptId) && (
              <p className="mt-4 text-[11px] font-mono uppercase tracking-wider text-white/40 border border-[#2a2a2a] inline-block px-3 py-2">
                {mandateId && (
                  <>
                    Current mandate · <span className="text-white/70">{mandateId}</span>
                    {projectName && <span className="text-white/30"> · {projectName}</span>}
                  </>
                )}
                {scriptId && workflow.script_title && (
                  <>
                    {mandateId && <span className="text-white/20 mx-2">|</span>}
                    Active script · <span className="text-white/70">{scriptId}</span>
                    <span className="text-white/30"> · {workflow.script_title}</span>
                    {scriptTotalScore != null && (
                      <span className="text-index-accent"> · Echo {scriptTotalScore}</span>
                    )}
                  </>
                )}
              </p>
            )}
          </div>
          {showStartNewAnalysis && (
            <Button variant="indexPrimary" onClick={handleStartNewAnalysis} className="shrink-0">
              <FilePlus className="mr-2 h-4 w-4" />
              Ingest screenplay
            </Button>
          )}
        </header>

        {(workflowError || analysisError) && (
          <div className="mb-6 flex flex-col gap-3 rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm font-sans">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{workflowError || analysisError}</span>
            </div>
            {failedStepIds.length > 0 && (
              <ul className="ml-6 list-disc text-red-200/80 text-xs space-y-1">
                {failedStepIds.slice(0, 8).map((sid) => (
                  <li key={sid}>{methodologyPhaseLabel(sid) || sid}</li>
                ))}
              </ul>
            )}
            {workflow.steps.upload === 'complete' && !analyzing && (
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetryAnalyse}
                  className="border-red-400/40 text-red-100 hover:bg-red-500/20 font-sans"
                >
                  <RotateCcw className="mr-2 h-3.5 w-3.5" />
                  Retry analysis
                </Button>
              </div>
            )}
          </div>
        )}

        {scorecardPending && workflow.steps.analyse === 'complete' && !analysisError && (
          <div className="mb-6 rounded-lg border border-[#2a2a2a] bg-[#141414] px-4 py-3 text-sm text-white/60 font-sans">
            Analysis facts are ready. Scorecard ratings (Step D) are not wired yet — Generate
            Scorecard stays unavailable until the ratings agent lands.
          </div>
        )}

        <section className="py-4 md:py-8">
          {workflowLoading ? (
            <p className="text-white/50 font-sans text-center py-8">Loading workflow…</p>
          ) : (
            <WorkflowStepper
              steps={displaySteps}
              activeStep={activeStep}
              analyseSubtext={analyseSubtext}
              onStepClick={handleStepClick}
              canClickStep={(key) => canClickStep(key)}
            />
          )}
        </section>

        {analyzing && (
          <div className="mt-6 flex justify-center gap-3">
            <Button
              variant="outline"
              onClick={() => void cancel()}
              className="border-[#2a2a2a] text-white hover:bg-[#1a1a1a] font-sans"
            >
              Cancel analysis
            </Button>
          </div>
        )}

        {!analyzing &&
          workflow.steps.analyse !== 'complete' &&
          workflow.steps.upload === 'complete' &&
          analysisError && (
            <div className="mt-6 flex justify-center">
              <Button variant="indexPrimary" onClick={handleRetryAnalyse} className="font-sans">
                <RotateCcw className="mr-2 h-4 w-4" />
                Retry analysis
              </Button>
            </div>
          )}

        <footer className="mt-12 border-t border-[#2a2a2a] pt-6 flex flex-col gap-4">
          <p className="text-[10px] font-mono uppercase text-white/30">
            Select a step to take action when available · F9 audit run
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <Button
              variant="indexOutline"
              onClick={() => setShowScorecardModal(true)}
              className="shrink-0"
            >
              <History className="mr-2 h-4 w-4" />
              Retrieve reports
            </Button>
            {showReviewSteps && (
              <Button
                variant="indexPrimary"
                onClick={() => navigate(`/workflow/steps/${scorecardTargetId}`)}
                className="shrink-0"
              >
                Review step outputs
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
            {showContinueToScorecard && (
              <Button variant="indexPrimary" onClick={() => void navigateToScorecard()} className="shrink-0">
                View Nuclius Index report
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </footer>
      </div>
    </IndexWorkspaceShell>
  );
};

export default Workflow;
